/**
 * FileDropzone (#2002 / #2013) — the platform-wide shared file-upload affordance.
 *
 * One accessible component every upload surface uses instead of a bare
 * `<input type="file">`, so files can ALWAYS be added by BOTH drag-and-drop and
 * click-to-browse, consistently, across the entire CRM. It is a thin, additive
 * wrapper: it keeps a real hidden `<input type="file">` in the DOM (so existing
 * tests/labels and the native picker keep working), layers drag-and-drop +
 * keyboard activation on top, and validates the dropped/chosen files against the
 * same `accept` / `maxSizeBytes` / `multiple` rules the surface already enforced
 * — surfacing a clear inline message for a rejected type or oversize file rather
 * than silently submitting it.
 *
 * Variants:
 *   - "box" (default): a dashed drop card with an icon + prompt. For the form /
 *     panel surfaces (Document Vault, record docs, FICA, branding, onboarding…).
 *   - "inline": renders only the caller's own trigger (passed as children) wired
 *     for click + drag-and-drop, with no drop card of its own. For compact
 *     table-row / toolbar contexts (checklists, per-row "Upload" buttons).
 *
 * Style guide: brand tokens only, dark mode, >=44px targets, mobile-friendly,
 * Lucide icons, agency-neutral, no new npm dependency.
 */
import { useId, useRef, useState } from "react";
import { AlertTriangle, Loader2, UploadCloud } from "lucide-react";

export interface FileDropzoneProps {
  /** Called with the validated, accepted files. Never called with an empty list. */
  onFiles: (files: File[]) => void;
  /**
   * Accept filter, mirrors the native input `accept` attribute
   * (e.g. ".pdf,.docx" or "image/*"). Used for the native picker AND to validate
   * dropped files. Omit to accept anything.
   */
  accept?: string;
  /** Max per-file size in bytes. A larger file is rejected with a message. */
  maxSizeBytes?: number;
  /**
   * Allow choosing/dropping more than one file. Default false.
   *
   * When false, a multi-file drop still uses only the first file — but says so
   * (#3073); it no longer discards the remainder in silence.
   */
  multiple?: boolean;
  /** Disable all interaction (e.g. while a previous upload is in flight). */
  disabled?: boolean;
  /** Accessible name for the input/drop target. Strongly recommended. */
  "aria-label"?: string;
  /** Optional id passed to the underlying input (for an external <label htmlFor>). */
  id?: string;
  /** Optional name on the underlying input. */
  name?: string;
  /** `capture` passthrough for mobile camera (e.g. "user"). */
  capture?: boolean | "user" | "environment";
  /** Visual variant — full drop card ("box", default) or trigger-only ("inline"). */
  variant?: "box" | "inline";
  /** Shows a spinner + "busy" affordance on the box variant. */
  busy?: boolean;
  /** Prompt text for the box variant. */
  label?: React.ReactNode;
  /** Secondary hint text for the box variant. */
  hint?: React.ReactNode;
  /** Extra classes for the outer wrapper / drop card. */
  className?: string;
  /**
   * For the "inline" variant: the caller's own trigger (button/label). It is
   * rendered inside the drop+click wrapper. Clicking it (or the wrapper) opens
   * the picker; dropping a file onto it validates + fires onFiles.
   */
  children?: React.ReactNode;
  /** Help-assistant anchor rendered as `data-help` on this control (D10). */
  helpAnchor?: string;
}

/** Human-readable size, for the rejection message. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Validate a file against an `accept` token list. Mirrors browser semantics:
 * extensions (".pdf"), exact MIME ("application/pdf") and wildcard MIME
 * ("image/*"). An empty accept list accepts everything.
 */
function matchesAccept(file: File, accept?: string): boolean {
  if (!accept) return true;
  const tokens = accept
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith(".")) return name.endsWith(token);
    if (token.endsWith("/*")) {
      const base = token.slice(0, token.indexOf("/"));
      return type.startsWith(`${base}/`);
    }
    return type === token;
  });
}

export default function FileDropzone({
  onFiles,
  accept,
  maxSizeBytes,
  multiple = false,
  disabled = false,
  id,
  name,
  capture,
  variant = "box",
  busy = false,
  label,
  hint,
  className = "",
  children,
  helpAnchor,
  ...rest
}: FileDropzoneProps) {
  const ariaLabel = rest["aria-label"];
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A non-fatal notice: files were accepted, but something about the selection
  // needs saying out loud -- today only "this surface takes one file at a
  // time", which used to happen SILENTLY (#3073).
  const [notice, setNotice] = useState<string | null>(null);
  const generatedId = useId();
  const errorId = `${id ?? generatedId}-dropzone-error`;
  const noticeId = `${id ?? generatedId}-dropzone-notice`;

  function openPicker() {
    if (disabled) return;
    inputRef.current?.click();
  }

  /**
   * Validate the candidate files and fire `onFiles` with the ones that pass.
   *
   * Two rules exist so a dropped file is NEVER discarded in silence (#3073):
   *
   *  - **Single-file surfaces warn.** When `multiple` is false and more than one
   *    file arrives, only the first is used (unchanged behaviour) but the caller
   *    sees an explicit notice naming how many were left out -- previously the
   *    extras vanished with no feedback at all.
   *  - **One bad file no longer voids the batch.** In `multiple` mode the
   *    rejected files are listed in the inline error and the ACCEPTED remainder
   *    is still submitted, instead of aborting the whole drop on the first
   *    wrong-type/oversize file. In single-file mode the behaviour is
   *    identical to before (one candidate: it either passes or it doesn't).
   */
  function processFiles(fileList: FileList | File[]) {
    const all = Array.from(fileList);
    if (all.length === 0) return;
    const candidates = multiple ? all : all.slice(0, 1);

    const accepted: File[] = [];
    const rejections: string[] = [];
    for (const file of candidates) {
      if (!matchesAccept(file, accept)) {
        rejections.push(`"${file.name}" is not an accepted file type.`);
        continue;
      }
      if (maxSizeBytes != null && file.size > maxSizeBytes) {
        rejections.push(
          `"${file.name}" is too large (max ${formatSize(maxSizeBytes)}).`,
        );
        continue;
      }
      accepted.push(file);
    }

    const skipped = all.length - candidates.length;
    // Name the file that was actually ADDED, never `candidates[0]` blindly:
    // the first candidate may itself have been rejected on type/size, in which
    // case nothing was added and the rejection alert below is the real story.
    const added = accepted[0];
    setNotice(
      skipped > 0
        ? added
          ? `Only "${added.name}" was added — this upload takes one file ` +
            `at a time, so ${skipped} other ${skipped === 1 ? "file was" : "files were"} ` +
            `not added. Upload the rest one at a time.`
          : `This upload takes one file at a time, so ${skipped} other ` +
            `${skipped === 1 ? "file was" : "files were"} not added. ` +
            `Upload the rest one at a time.`
        : null,
    );
    setError(rejections.length > 0 ? rejections.join(" ") : null);
    if (accepted.length > 0) onFiles(accepted);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    if (e.dataTransfer?.files?.length) processFiles(e.dataTransfer.files);
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (disabled) return;
    setDragOver(true);
  }

  function onDragLeave() {
    setDragOver(false);
  }

  // The real native input — always present so the OS picker, existing tests
  // (getByLabelText / querySelector('input[type=file]')) and labels keep working.
  const nativeInput = (
    <input
      data-help={helpAnchor}
      ref={inputRef}
      id={id}
      name={name}
      type="file"
      accept={accept}
      multiple={multiple}
      disabled={disabled}
      aria-label={ariaLabel}
      {...(capture ? { capture } : {})}
      className="sr-only"
      onChange={(e) => {
        if (e.target.files) processFiles(e.target.files);
        // Reset so re-selecting the same file fires change again.
        e.target.value = "";
      }}
    />
  );

  const errorBlock = error ? (
    <div
      id={errorId}
      role="alert"
      className="mt-2 flex items-start gap-2 rounded-lg border-l-[3px] border-[var(--color-danger)] bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span>{error}</span>
    </div>
  ) : null;

  // Advisory, not an error: the upload still happened. `role="status"` keeps it
  // a polite live region so it never competes with the error block above.
  const noticeBlock = notice ? (
    <div
      id={noticeId}
      role="status"
      className="mt-2 flex items-start gap-2 rounded-lg border-l-[3px] border-[var(--color-warning)] bg-[var(--color-warning)]/10 px-3 py-2 text-sm text-[var(--color-warning)]"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span>{notice}</span>
    </div>
  ) : null;

  if (variant === "inline") {
    // Trigger-only: wrap the caller's button/label for click + drop, no card.
    return (
      <span className={className}>
        {/*
          The keyboard-accessible control is the caller's own button/label
          (passed as children). This wrapper only layers drag-and-drop + a
          click-to-open convenience; a native button fires a bubbling `click`
          on Enter/Space, so keyboard activation reaches openPicker through it.
        */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <span
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={openPicker}
          className={[
            "inline-flex rounded-lg transition-colors",
            dragOver ? "ring-2 ring-brand-accent ring-offset-1" : "",
          ].join(" ")}
        >
          {children}
        </span>
        {nativeInput}
        {errorBlock}
        {noticeBlock}
      </span>
    );
  }

  return (
    <div className={className}>
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel ? `${ariaLabel} drop area` : "File upload drop area"}
        aria-disabled={disabled || undefined}
        aria-describedby={error ? errorId : undefined}
        className={[
          "flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent",
          disabled
            ? "cursor-not-allowed opacity-60 border-[var(--color-border)] bg-[var(--color-bg-card)]"
            : dragOver
              ? "cursor-pointer border-brand-accent bg-brand-accent/5"
              : "cursor-pointer border-[var(--color-border)] bg-[var(--color-bg-card)] hover:border-brand-accent",
        ].join(" ")}
      >
        {busy ? (
          <Loader2
            className="h-6 w-6 animate-spin text-brand-accent"
            aria-hidden
          />
        ) : (
          <UploadCloud className="h-6 w-6 text-brand-accent" aria-hidden />
        )}
        <span className="text-sm font-medium text-[var(--color-text-primary)]">
          {label ?? "Drag & drop a file here, or click to choose"}
        </span>
        {hint != null ? (
          <span className="text-xs text-[var(--color-text-muted)]">{hint}</span>
        ) : multiple ? (
          <span className="text-xs text-[var(--color-text-muted)]">
            You can upload multiple files at once.
          </span>
        ) : null}
      </div>
      {nativeInput}
      {errorBlock}
      {noticeBlock}
    </div>
  );
}
